/**
 * Columnar tables — the `{cols, rows}` wire form and its rehydrator.
 *
 * Eight of the extracted OEM+ datasets ship in the source demo's **columnar**
 * form: a header array plus an array of positional value arrays, rather than an
 * array of objects. This module is the one place the port converts between the
 * two.
 *
 * ## Why the port keeps the columnar form
 *
 * Rehydrating at extract time was the original intent, but it does not fit
 * inside the byte budget: hydrating everything pushes the eager core past its
 * 150 KB cap and adds ~1.4 MB to the lazy total. The full per-table byte math
 * is tabulated in **`scripts/oem/README.md` § "Columnar tables — a deliberate
 * deviation"** — it is not repeated here so there is a single source of truth
 * for the numbers.
 *
 * The cost of keeping the source's own representation is one
 * {@link unpackTable} call per dataset. **Consumers never see the columnar
 * form**: the typed accessors in `src/versions/v1/data/core.ts` and the
 * `useOemData()` hook hand back arrays of objects.
 *
 * ## The eight columnar datasets
 *
 * | `B` key | Ships in | Rows × cols |
 * |---|---|---|
 * | `wos` | `public/data/oem/work-orders.json` | 3,920 × 23 |
 * | `events_daily` | `public/data/oem/telemetry.json` | 2,364 × 5 |
 * | `shift_daily` | `public/data/oem/telemetry.json` | 1,100 × 9 |
 * | `daily_metric` | `public/data/oem/telemetry.json` | 1,100 × 5 |
 * | `health` | `src/versions/v1/data/core.json` (**eager**) | 1,068 × 6 |
 * | `tyres` | `public/data/oem/telemetry.json` | 282 × 12 |
 * | `css` | `public/data/oem/people.json` | 34 × 13 |
 * | `roster` | `public/data/oem/people.json` | 7 × 10 |
 *
 * `health` is the only one in the eager core; it is unpacked once at module
 * scope by `data/core.ts`. The other seven arrive with their lazy domain file.
 *
 * Note that `tyres`, `css` and `roster` carry **human-readable column names**
 * with spaces and parentheses (`'Tread mm'`, `'Quality (1-5)'`) — they came
 * from spreadsheet exports and the demo renders their headers verbatim. Their
 * hydrated row types therefore use quoted keys.
 *
 * ## Ported from `OEM+_Platform_Demo.html`
 *
 * Source line 1839, in full:
 *
 * ```js
 * const unpack = t => t.rows.map(r => Object.fromEntries(t.cols.map((c,i)=>[c,r[i]])));
 * ```
 *
 * Shared layer (layer 2 support): pure, no React, no side effects, no caching.
 *
 * @see scripts/oem/README.md
 * @see docs/foundations/4-layer-architecture.md
 */

/**
 * A dataset in the demo's columnar wire form, parameterised by the row it
 * **becomes**.
 *
 * ### Which of the two columnar types to reach for
 *
 * The schema layer declares `ColumnarWire<TColumns, TCells>` in
 * `../schema/telemetry.ts`, parameterised by the literal column tuple and the
 * positional cell tuple. That one pins the **wire** form, so an extractor
 * column reorder becomes a compile error, and it is what the named aliases
 * (`WorkOrderTable`, `HealthTable`, `CssTable`, …) and `OemDomainData` use.
 * This one is parameterised by the **hydrated row** and exists so
 * {@link unpackTable} has something to infer from. The two are structurally
 * compatible in both directions: a `ColumnarWire` passes straight into
 * `unpackTable`, naming the row — `unpackTable<WorkOrder>(data.wos)`.
 *
 * The type parameter `T` is **phantom** — it does not appear in the structure,
 * it records what {@link unpackTable} will produce. That is what lets
 * `data/core.ts` declare `health: ColumnarTable<ComponentHealth>` and have
 * `unpackTable(core.health)` infer `ComponentHealth[]` with no annotation at
 * the call site, while the JSON itself stays shapeless. A `ColumnarWire`
 * carries no phantom, so a call against one needs its row named.
 *
 * `rows` is loosely typed rather than `readonly T[keyof T][][]`: a row is a
 * heterogeneous positional tuple (string, number, `null`, …) and TypeScript
 * cannot relate position to key without a tuple type per table. Narrowing
 * happens once, at the {@link unpackTable} boundary.
 *
 * ### Why the inner array is `readonly unknown[]`, not `unknown[]`
 *
 * So that a **precisely typed** table from `schema/` is accepted here. The
 * schema lane models the same wire form as tuple types — e.g.
 * `WorkOrderCells = readonly [wo_id: string, mine: MineCode, …]` — and a
 * `readonly` tuple is **not** assignable to a mutable `unknown[]`. With a
 * mutable inner array this interface would reject every schema table:
 *
 * ```text
 * Type 'readonly WorkOrderCells[]' is not assignable to 'readonly unknown[][]'.
 *   The type 'WorkOrderCells' is 'readonly' and cannot be assigned to the
 *   mutable type 'unknown[]'.
 * ```
 *
 * The widening is pure: everything a mutable inner array accepted is still
 * accepted, and {@link unpackTable} only ever reads.
 *
 * @typeParam T - the hydrated row type this table produces.
 */
export interface ColumnarTable<T> {
  /** Column names, in row order. Becomes the keys of each hydrated object. */
  readonly cols: readonly string[];
  /** One positional value array per row, aligned to {@link ColumnarTable.cols}. */
  readonly rows: readonly (readonly unknown[])[];
  /** Phantom marker carrying `T`; never present at runtime. */
  readonly __row?: T;
}

/**
 * Rehydrate a {@link ColumnarTable} into an array of objects — the port of the
 * demo's one-line `unpack()` (source line 1839).
 *
 * **Pure and uncached.** Every call allocates a fresh array of fresh objects.
 * Callers that need the result more than once must hoist it: `data/core.ts`
 * unpacks `health` once at module scope, and a component unpacking a lazily
 * fetched table should do it in a `useMemo` keyed on the table identity (the
 * fetch cache in `useOemData.ts` makes that identity stable across renders and
 * across components). Caching inside this function would mean holding a
 * module-level `WeakMap` of every table ever seen, which is state in what
 * should be a derivation.
 *
 * ### Fidelity notes, kept from the demo
 *
 * - A row **shorter** than `cols` yields `undefined` for the missing keys; a
 *   row **longer** than `cols` has its extra values dropped. The extractor
 *   guarantees rectangular tables, so neither happens in practice — but the
 *   behaviour is the demo's and is not "fixed" here.
 * - Duplicate column names collapse, last one winning, as with any object
 *   literal. None of the eight tables has duplicates.
 * - Order is preserved: hydrated row *i* corresponds to `table.rows[i]`.
 *
 * The single `as unknown as T[]` is the type-system boundary between shapeless
 * JSON and the domain model. It is unavoidable — `Object.fromEntries` is typed
 * as returning an index-signature object — and it is confined to this one line
 * rather than repeated at every consumer.
 *
 * @typeParam T - the hydrated row type, carried by the table.
 * @param table - the columnar table, straight off the JSON.
 * @returns a new array of hydrated row objects.
 *
 * @example
 * import coreJson from '../data/core.json';
 * const health = unpackTable<ComponentHealth>(coreJson.health);
 * health.length;   // 1068
 * health[0];       // { machine_id: 'J-01', component: 'LH boom', health_score: 74, … }
 *
 * @example Tables typed by `schema/` carry no phantom `T`, so name it:
 * ```ts
 * import type { WorkOrderTable, WorkOrder } from '../schema/work-order';
 * declare const wos: WorkOrderTable;
 * unpackTable<WorkOrder>(wos);   // WorkOrder[] — explicit, not inferred
 * ```
 */
export function unpackTable<T>(table: ColumnarTable<T>): T[] {
  const { cols, rows } = table;
  return rows.map((row) => {
    const out: Record<string, unknown> = {};
    for (let i = 0; i < cols.length; i += 1) {
      out[cols[i]] = row[i];
    }
    return out as unknown as T;
  });
}
